import * as THREE from 'three';
import { vehicleAmbient } from '../vehicles/vehicleView';
import { PAVE_Y } from '../world/view/ground';
import { BAY_WALL_OFFSET, type SprayBay } from './bay';

/**
 * What the spray bay looks like (render only; the low walls and their colliders come with the world):
 * a lamp gantry over the mouth with Pike's sign tilted up at the high camera, yellow floor marks with a
 * stop bar, and a warm wash of light on the floor — brighter at night, brightest while a car is being done.
 * One small group per bay, a handful of draw calls, nothing allocated per frame.
 */
export interface BayViews {
  /** Garage mode on / off at this bay; `level` scales the floor wash (the neon shot dims it to dusk). */
  setActive(poi: string, on: boolean, level?: number): void;
  update(dt: number, heat: number): void;
  dispose(): void;
}

function signTexture(): THREE.Texture | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#e9e0c9';
  g.fillRect(0, 0, 512, 128);
  g.fillStyle = '#b8261a';
  g.fillRect(0, 0, 512, 14);
  g.fillRect(0, 114, 512, 14);
  g.fillStyle = '#15120f';
  g.textAlign = 'center';
  g.font = 'bold 54px Impact, "Arial Narrow", sans-serif';
  g.fillText("PIKE'S SPRAY BAY", 256, 70);
  g.font = 'bold 26px "Courier New", monospace';
  g.fillText('DRIVE IN SLOWLY · STOP ON THE MARKS', 256, 104);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

function poolTexture(): THREE.DataTexture {
  const n = 32;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = Math.abs((x + 0.5) / n - 0.5) * 2;
      const dy = Math.abs((y + 0.5) / n - 0.5) * 2;
      const d = Math.min(1, Math.hypot(Math.max(0, dx - 0.35) / 0.65, Math.max(0, dy - 0.25) / 0.75));
      const i = (y * n + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(Math.pow(1 - d, 1.6) * 255);
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.needsUpdate = true;
  return t;
}

export function buildBayViews(scene: THREE.Object3D, bays: readonly SprayBay[]): BayViews {
  const steel = new THREE.MeshStandardMaterial({ color: 0x2f4a3a, roughness: 0.6, metalness: 0.3 });
  const yellow = new THREE.MeshStandardMaterial({ color: 0xe0b52a, roughness: 0.8 });
  const white = new THREE.MeshStandardMaterial({ color: 0xe8e4d6, roughness: 0.85 });
  const tex = signTexture();
  const signMat = new THREE.MeshBasicMaterial({ color: 0xffffff, map: tex ?? null });
  const lamp = new THREE.MeshBasicMaterial({ color: 0xffe2a8 });
  const poolTex = poolTexture();
  const geos: THREE.BufferGeometry[] = [];
  const box = (w: number, h: number, d: number): THREE.BoxGeometry => {
    const g = new THREE.BoxGeometry(w, h, d);
    geos.push(g);
    return g;
  };
  const views = new Map<string, { group: THREE.Group; gantry: THREE.Object3D[]; pool: THREE.MeshBasicMaterial; active: number; want: number }>();

  for (const b of bays) {
    const g = new THREE.Group();
    g.name = `spray-bay-${b.poi}`;
    g.position.set(b.center.x, 0, b.center.z);
    g.rotation.y = b.heading;
    // Local frame: +z out of the bay (toward the road), x across.
    const zMouth = b.halfL - 0.2;
    const gantry: THREE.Object3D[] = [];
    const H = 3.5;
    const W = BAY_WALL_OFFSET * 2;
    for (const sx of [-1, 1]) {
      const post = new THREE.Mesh(box(0.22, H, 0.22), steel);
      post.position.set(sx * BAY_WALL_OFFSET, H / 2, zMouth);
      post.castShadow = true;
      g.add(post);
      gantry.push(post);
    }
    const beam = new THREE.Mesh(box(W + 0.3, 0.3, 0.3), steel);
    beam.position.set(0, H - 0.1, zMouth);
    beam.castShadow = true;
    g.add(beam);
    gantry.push(beam);
    // The sign stands on the beam, leaning back so the high camera can read it.
    const sign = new THREE.Mesh(box(W * 0.82, W * 0.82 * 0.25, 0.06), [steel, steel, steel, steel, signMat, steel]);
    sign.position.set(0, H + 0.55, zMouth + 0.05);
    sign.rotation.x = -0.7;
    g.add(sign);
    gantry.push(sign);
    // Lamps under the beam.
    for (const x of [-2, 0, 2]) {
      const l = new THREE.Mesh(box(0.9, 0.08, 0.2), lamp);
      l.position.set(x, H - 0.3, zMouth);
      g.add(l);
      gantry.push(l);
    }
    // Floor marks: a yellow box, a white stop bar at the back, hatching across the mouth.
    const y = PAVE_Y + 0.012; // the yard is paved (pavement height)
    const line = (w: number, d: number, x: number, z: number, m: THREE.Material): void => {
      const s = new THREE.Mesh(box(w, 0.02, d), m);
      s.position.set(x, y, z);
      s.receiveShadow = true;
      g.add(s);
    };
    line(0.14, b.halfL * 2, -b.halfW + 0.1, 0, yellow);
    line(0.14, b.halfL * 2, b.halfW - 0.1, 0, yellow);
    line(b.halfW * 2, 0.14, 0, b.halfL - 0.1, yellow);
    line(b.halfW * 1.2, 0.3, 0, -b.halfL + 0.9, white);
    for (let i = -2; i <= 2; i++) {
      const s = new THREE.Mesh(box(0.12, 0.02, 0.9), yellow);
      s.position.set(i * 1.1, y, b.halfL - 0.65);
      s.rotation.y = 0.6;
      g.add(s);
    }
    // The wash of light on the floor.
    const poolMat = new THREE.MeshBasicMaterial({ map: poolTex, color: 0xffd9a0, transparent: true, opacity: 0.2, blending: THREE.AdditiveBlending, depthWrite: false });
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(b.halfW * 2.4, b.halfL * 2.4).rotateX(-Math.PI / 2), poolMat);
    geos.push(pool.geometry);
    pool.position.y = PAVE_Y + 0.03;
    pool.renderOrder = 2;
    g.add(pool);
    scene.add(g);
    views.set(b.poi, { group: g, gantry, pool: poolMat, active: 0, want: 0 });
  }

  return {
    setActive(poi, on, level = 1) {
      const v = views.get(poi);
      if (!v) return;
      v.want = on ? level : 0;
      // In garage mode the camera comes down to the car: the gantry would stand in front of it (the work
      // lamps light the car instead).
      for (const o of v.gantry) o.visible = !on;
    },
    update(dt) {
      const night = vehicleAmbient.night;
      for (const v of views.values()) {
        v.active += (v.want - v.active) * Math.min(1, dt * 4);
        v.pool.opacity = 0.12 + 0.38 * night + 0.3 * v.active;
      }
    },
    dispose() {
      for (const v of views.values()) {
        scene.remove(v.group);
        v.pool.dispose();
      }
      for (const g of geos) g.dispose();
      for (const m of [steel, yellow, white, signMat, lamp]) m.dispose();
      tex?.dispose();
      poolTex.dispose();
    },
  };
}
